<template>
  <!-- Selector de color COMPACTO (presentación pura): fila de top-picks + un disparador
       que abre un popup con rejilla de paleta, input hex y selector nativo del navegador.
       No conoce el motor; emite `input` con el color elegido. -->
  <div class="epc-color">
    <div class="epc-color-picks">
      <button v-for="c in picks" :key="'pick_' + c" type="button"
        class="epc-color-swatch"
        :class="{ active: value === c, 'is-transparent': c === 'transparent' }"
        :style="c === 'transparent' ? {} : { backgroundColor: c }"
        :title="c" @click="pick(c)"></button>

      <span class="epc-color-sep"></span>

      <!-- Disparador: muestra el color actual y abre/cierra el popup. -->
      <button ref="trigger" type="button" class="epc-color-swatch epc-color-trigger"
        :class="{ 'is-transparent': value === 'transparent', open }"
        :style="value === 'transparent' ? {} : { backgroundColor: value }"
        :title="label" @click="toggle"></button>
    </div>

    <!-- Popup FIJO a la derecha del panel (posición calculada desde el botón, para no
         recortarse con el overflow del panel). Backdrop para cerrar al pulsar fuera. -->
    <template v-if="open">
      <div class="epc-color-backdrop" @click="close"></div>
      <div class="epc-color-popup" :style="popupStyle">
        <div class="epc-color-grid">
          <button v-for="c in palette" :key="'pal_' + c" type="button"
            class="epc-color-swatch"
            :class="{ active: value === c, 'is-transparent': c === 'transparent' }"
            :style="c === 'transparent' ? {} : { backgroundColor: c }"
            :title="c" @click="pick(c)"></button>
        </div>

        <div class="epc-color-hex">
          <span class="epc-color-hash">#</span>
          <input class="epc-color-input" type="text" spellcheck="false"
            placeholder="hex" v-model="hexDraft"
            @keyup.enter="applyHex" @blur="applyHex" />
          <label class="epc-color-native" :title="label">
            <input type="color" :value="nativeValue" @input="pick($event.target.value)" />
          </label>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ViewerColorPicker',

  props: {
    value: { type: String, default: '#1e1e1e' },
    label: { type: String, default: null },
    picks: { type: Array, default: () => [] },   // fila superior (top-picks)
    palette: { type: Array, default: () => [] },  // rejilla del popup
  },

  data() {
    return {
      open: false,
      hexDraft: this.toHex(this.value),
      popupStyle: {}, // left/top fijos, calculados desde el botón al abrir
    }
  },

  computed: {
    // El <input type="color"> nativo necesita un #rrggbb válido (no 'transparent').
    nativeValue() {
      return this.value && this.value.startsWith('#') ? this.value : '#000000'
    },
  },

  watch: {
    value(v) {
      this.hexDraft = this.toHex(v)
    },
  },

  methods: {
    toHex(v) {
      return v && v.startsWith('#') ? v.replace('#', '') : (v || '')
    },
    toggle() {
      this.open = !this.open
      if (this.open) {
        this.hexDraft = this.toHex(this.value)
        this.$nextTick(this._positionPopup)
      }
    },
    close() {
      this.open = false
    },
    // Coloca el popup a la DERECHA del botón (coords de viewport, position:fixed). Si no
    // cabe a la derecha, lo abre a la izquierda.
    _positionPopup() {
      const el = this.$refs.trigger
      if (!el) return
      const r = el.getBoundingClientRect()
      const W = 210
      let left = r.right + 12
      if (left + W > window.innerWidth) left = Math.max(8, r.left - 12 - W)
      const top = Math.max(8, r.top - 8)
      this.popupStyle = { left: left + 'px', top: top + 'px' }
    },
    pick(c) {
      this.$emit('input', c)
    },
    applyHex() {
      const raw = (this.hexDraft || '').trim().replace(/^#/, '')
      if (/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(raw)) {
        this.$emit('input', '#' + raw)
      } else {
        // valor inválido → restaurar al actual
        this.hexDraft = this.toHex(this.value)
      }
    },
  },
}
</script>

<style scoped>
.epc-color {
  position: relative;
}

.epc-color-picks {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  flex-wrap: nowrap;
}

.epc-color-sep {
  flex: 0 0 auto;
  width: 1px;
  height: 16px;
  background-color: #e3e3e8;
  margin: 0 1px;
}

.epc-color-swatch {
  flex: 0 0 auto; /* no encoger: mantener cuadrado aunque la fila vaya justa */
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  padding: 0;
  background-color: #ffffff;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}

.epc-color-swatch:hover {
  transform: scale(1.06);
}

.epc-color-swatch.active {
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #3a6bc8;
}

.epc-color-trigger {
  margin-left: auto;
}

.epc-color-trigger.open {
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #3a6bc8;
}

/* "Transparente": fondo a cuadros */
.epc-color-swatch.is-transparent {
  background-image:
    linear-gradient(45deg, #d0d0d8 25%, transparent 25%),
    linear-gradient(-45deg, #d0d0d8 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #d0d0d8 75%),
    linear-gradient(-45deg, transparent 75%, #d0d0d8 75%);
  background-size: 10px 10px;
  background-position: 0 0, 0 5px, 5px -5px, -5px 0;
  background-color: #ffffff;
}

.epc-color-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
}

.epc-color-popup {
  position: fixed;
  z-index: 1000;
  width: 200px;
  background-color: #ffffff;
  border: 1px solid #e9e9ef;
  border-radius: 10px;
  box-shadow: 0 4px 14px 0 rgba(0, 0, 0, 0.15);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.epc-color-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.epc-color-hex {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  border: 1px solid #e3e3e8;
  border-radius: 7px;
  padding: 2px 6px;
}

.epc-color-hash {
  color: #868e96;
  font-size: 13px;
}

.epc-color-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: 13px;
  color: #343a40;
  background: transparent;
}

.epc-color-native {
  display: inline-flex;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e3e3e8;
}

.epc-color-native input[type="color"] {
  width: 140%;
  height: 140%;
  margin: -20%;
  padding: 0;
  border: none;
  cursor: pointer;
  background: transparent;
}
</style>
